<!-- src/pages/ShoppingListsView.vue -->
<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useQuery, useMutation } from '@vue/apollo-composable'
import { GET_SHOPPING_LISTS, GET_SHOPPING_LIST } from '../graphql/queries'
import { DELETE_SHOPPING_LIST } from '../graphql/mutations'
import { showError, showSuccess } from '../utils/errorHandling'
import ConfirmDialog from '../components/ConfirmDialog.vue'
import dayjs from 'dayjs'
import CreateShoppingListModal from '../components/shopping-list/CreateShoppingListModal.vue'
import SyncShoppingListModal from '../components/shopping-list/SyncShoppingListModal.vue'
import EditShoppingListModal from '../components/shopping-list/EditShoppingListModal.vue'

const route = useRoute()
const router = useRouter()

// State
const showCreateModal = ref(false)
const showSyncModal = ref(false)
const showEditShoppingListModal = ref(false)
const showDeleteDialog = ref(false)
const selectedList = ref<any>(null)
const searchQuery = ref('')
const statusFilter = ref<'all' | 'pending' | 'active' | 'completed' | ''>('')

// GraphQL queries
const { result: shoppingListsResult, loading: loadingLists, refetch: refetchLists } = useQuery(GET_SHOPPING_LISTS)
const { refetch: refetchDetails } = useQuery(
  GET_SHOPPING_LIST,
  () => ({ id: selectedList.value?.id }),
  () => ({ enabled: !!selectedList.value?.id })
)

// Mutations
const { mutate: deleteShoppingListMutation, loading: deletingList } = useMutation(DELETE_SHOPPING_LIST)

// Computed properties
const shoppingLists = computed(() => shoppingListsResult.value?.shoppingLists || [])

const filteredLists = computed(() => {
  let filtered = shoppingLists.value

  // Search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter((list: any) =>
      list.title.toLowerCase().includes(query) ||
      list.description?.toLowerCase().includes(query)
    )
  }

  // Status filter
  if (statusFilter.value) {
    filtered = filtered.filter((list: any) => list.status === statusFilter.value)
  }

  // Sort by creation date (newest first)
  return [...filtered].sort((a: any, b: any) =>
    dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf()
  )
})

const totalLists = computed(() => shoppingLists.value.length)


// Methods
const viewList = (list: any) => {
  router.push(`/shopping-lists/${list.id}`)
}

const editList = async (list: any) => {
  selectedList.value = list
  await refetchDetails()
  showEditShoppingListModal.value = true
}

const deleteList = (list: any) => {
  selectedList.value = list
  showDeleteDialog.value = true
}

const confirmDelete = async () => {
  if (!selectedList.value) return

  try {
    await deleteShoppingListMutation({ id: selectedList.value.id })
    showDeleteDialog.value = false
    selectedList.value = null
    showSuccess('Shopping list deleted successfully!')
    refetchLists()
  } catch (error) {
    showError(error, 'delete shopping list')
  }
}

const cancelDelete = () => {
  showDeleteDialog.value = false
  selectedList.value = null
}


const onShoppingListCreated = (shoppingList: any) => {
  showCreateModal.value = false
  showSyncModal.value = false
  refetchLists()
  showSuccess(`Shopping list "${shoppingList.title}" created successfully!`)
}



const onShoppingListUpdated = () => {
  showEditShoppingListModal.value = false
  refetchLists()
  if (selectedList.value) {
    refetchDetails()
  }
  showSuccess('Shopping list updated successfully!')
}

// Filter event handlers
const handleFilterChange = (filter: 'all' | 'pending' | 'active' | 'completed') => {
  if (filter === 'all') {
    statusFilter.value = ''
  } else if (filter === 'pending') {
    statusFilter.value = 'pending'
  } else if (filter === 'active') {
    statusFilter.value = 'active'
  } else if (filter === 'completed') {
    statusFilter.value = 'completed'
  }
}

// Watch for route changes
watch(() => route.query.action, (action) => {
  if (action === 'create') {
    showCreateModal.value = true
  }
})
</script>

<template>
  <div class="page-enter page-container">
    <!-- Header -->
    <div class="page-header">
      <div>
        <h1 class="font-display display-l page-title">Shopping Lists</h1>
        <p class="page-subtitle">Manage your shopping lists and track purchases</p>
      </div>
      <div class="header-actions">
        <button @click="showSyncModal = true" class="ds-btn-secondary">Sync Meals</button>
        <button @click="showCreateModal = true" class="ds-btn-primary">+ Create List</button>
      </div>
    </div>

    <!-- Status filter tabs -->
    <div class="filter-tabs">
      <button v-for="tab in [
          { key: '', label: 'All', count: totalLists },
          { key: 'pending', label: 'Pending', count: shoppingLists.filter((l: any) => l.status === 'pending').length },
          { key: 'active', label: 'Active', count: shoppingLists.filter((l: any) => l.status === 'active').length },
          { key: 'completed', label: 'Completed', count: shoppingLists.filter((l: any) => l.status === 'completed').length },
        ]" :key="tab.key"
        @click="handleFilterChange(tab.key === '' ? 'all' : tab.key as any)"
        class="filter-tab"
        :class="{ active: statusFilter === tab.key }">
        {{ tab.label }}
        <span class="filter-tab-count" :class="{ active: statusFilter === tab.key }">{{ tab.count }}</span>
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loadingLists" class="text-center py-16">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto spinner-border"></div>
      <p class="loading-text">Loading shopping lists...</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="filteredLists.length === 0" class="empty-state">
      <div class="empty-icon">🛒</div>
      <div class="empty-title">{{ shoppingLists.length === 0 ? 'No shopping lists yet' : 'No shopping lists found' }}</div>
      <p class="empty-desc">
        {{ shoppingLists.length === 0
          ? 'Use Sync Meals to auto-build a list from the meals you’ve planned — ingredients are gathered and merged for you. Or create one from scratch.'
          : 'Try adjusting the filter.' }}
      </p>
      <div class="empty-actions">
        <button @click="showSyncModal = true" class="ds-btn-secondary">Sync Meals</button>
        <button @click="showCreateModal = true" class="ds-btn-primary">+ Create List</button>
      </div>
    </div>

    <!-- Shopping Lists Grid -->
    <div v-else class="shopping-grid">
      <div v-for="list in filteredLists" :key="list.id" class="ds-card shopping-card">
        <!-- Card header -->
        <div class="card-header">
          <div class="card-title-wrap">
            <div class="card-title">{{ list.title }}</div>
            <div class="card-date">{{ dayjs(list.created_at).format('MMM D, YYYY') }}</div>
          </div>
          <!-- Status badge -->
          <span class="status-badge"
            :class="list.status === 'completed' ? 'status-completed' : list.status === 'active' ? 'status-active' : 'status-pending'">
            {{ list.status ? list.status.charAt(0).toUpperCase() + list.status.slice(1) : 'Pending' }}
          </span>
        </div>

        <!-- Progress bar -->
        <div class="progress-track">
          <div class="progress-fill"
            :style="`width:${list.items?.length > 0 ? Math.round((list.items.filter((i: any) => i.is_bought).length / list.items.length) * 100) : 0}%`" />
        </div>

        <!-- Stats grid -->
        <div class="stats-grid">
          <div class="stat-cell">
            <div class="stat-value muted">{{ list.items?.length || 0 }}</div>
            <div class="stat-label">Items</div>
          </div>
          <div class="stat-cell">
            <div class="stat-value primary">{{ list.items?.filter((i: any) => i.is_bought).length || 0 }}</div>
            <div class="stat-label">Bought</div>
          </div>
          <div class="stat-cell">
            <div class="stat-value dark">€{{ ((list.total_price || 0) / 100).toFixed(0) }}</div>
            <div class="stat-label">Total</div>
          </div>
        </div>

        <!-- Action buttons -->
        <div class="card-actions">
          <button @click="viewList(list)" class="ds-btn-primary card-btn">View</button>
          <button @click="editList(list)" class="ds-btn-secondary card-btn">Edit</button>
          <button @click="deleteList(list)" class="delete-btn">×</button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Dialog -->
    <ConfirmDialog :show="showDeleteDialog" title="Delete Shopping List"
      :message="selectedList ? `Are you sure you want to delete &quot;${selectedList.title}&quot;? This action cannot be undone.` : ''"
      confirm-text="Delete" :loading="deletingList" @confirm="confirmDelete" @cancel="cancelDelete" />
  </div>

  <!-- Create Shopping List Modal -->
  <CreateShoppingListModal :show="showCreateModal" @close="showCreateModal = false" @created="onShoppingListCreated" />

  <!-- Sync Shopping List Modal -->
  <SyncShoppingListModal :show="showSyncModal" @close="showSyncModal = false" @created="onShoppingListCreated" />

  <!-- Edit Shopping List Modal -->
  <EditShoppingListModal :show="showEditShoppingListModal" :shopping-list="selectedList"
    @close="showEditShoppingListModal = false" @updated="onShoppingListUpdated" />
</template>

<style scoped>
/* ── Page header ── */
.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 40px;
  flex-wrap: wrap;
  gap: 16px;
}
.page-title { letter-spacing: -1px; }
.page-subtitle { font-size: 14px; color: #64748B; margin-top: 6px; }
.header-actions { display: flex; gap: 10px; flex-wrap: wrap; }

/* ── Filter tabs ── */
.filter-tabs { display: flex; gap: 8px; margin-bottom: 32px; flex-wrap: wrap; }
.filter-tab {
  border: 1px solid #E2E8F0;
  border-radius: 9999px;
  padding: 7px 16px;
  font-family: Inter, sans-serif;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  transition: all .15s;
  display: flex;
  align-items: center;
  gap: 6px;
  background: white;
  color: #64748B;
}
.filter-tab.active { background: #4F8646; color: white; border-color: #4F8646; }
.filter-tab-count { border-radius: 9999px; padding: 1px 7px; font-size: 11px; background: #F1F5F9; }
.filter-tab-count.active { background: rgba(255,255,255,.25); }

/* ── Loading / empty ── */
.spinner-border { border-color: #4F8646; }
.loading-text { margin-top: 24px; color: #64748B; font-size: 14px; }
.empty-state { text-align: center; padding: 64px 0; color: #94A3B8; }
.empty-icon { font-size: 48px; margin-bottom: 16px; }
.empty-title { font-size: 16px; font-weight: 700; color: #334155; margin-bottom: 8px; }
.empty-desc { font-size: 14px; color: #64748B; margin-bottom: 24px; max-width: 400px; margin-left: auto; margin-right: auto; }
.empty-actions { display: flex; gap: 10px; justify-content: center; }

/* ── Shopping grid ── */
.shopping-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}
@media (min-width: 1024px) {
  .shopping-grid { grid-template-columns: repeat(3, 1fr); }
}

/* ── Shopping card ── */
.shopping-card {
  padding: 24px;
  border-radius: 20px;
  transition: box-shadow .2s, transform .2s;
}
.shopping-card:hover {
  box-shadow: 0 10px 28px rgba(79, 134, 70, .12);
  transform: translateY(-2px);
}

.card-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px; }
.card-title-wrap { flex: 1; min-width: 0; }
.card-title {
  font-family: Manrope, sans-serif;
  font-weight: 700;
  font-size: 16px;
  color: #0F172A;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.card-date { font-size: 12px; color: #94A3B8; }

/* Status badges */
.status-badge {
  border-radius: 9999px;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 700;
  font-family: Inter, sans-serif;
  white-space: nowrap;
  margin-left: 8px;
  flex-shrink: 0;
}
.status-completed { background: #EDF3EC; color: #4F8646; }
.status-active    { background: #EFF6FF; color: #3B82F6; }
.status-pending   { background: #FEF9C3; color: #A16207; }

/* Progress bar */
.progress-track { height: 4px; background: #F1F5F9; border-radius: 9999px; margin-bottom: 16px; }
.progress-fill  { height: 100%; background: #4F8646; border-radius: 9999px; transition: width .4s; }

/* Stats */
.stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 18px; }
.stat-cell  { text-align: center; padding: 10px 8px; background: #F8FAFC; border-radius: 12px; }
.stat-value { font-family: Manrope, sans-serif; font-weight: 700; font-size: 20px; }
.stat-value.muted   { color: #64748B; }
.stat-value.primary { color: #4F8646; }
.stat-value.dark    { color: #0F172A; font-size: 18px; }
.stat-label { font-size: 10px; font-weight: 700; color: #94A3B8; letter-spacing: .5px; text-transform: uppercase; }

/* Card actions */
.card-actions { display: flex; gap: 8px; }
.card-btn { flex: 1; justify-content: center; height: 36px; font-size: 13px; }
.delete-btn {
  width: 36px;
  height: 36px;
  background: rgba(186, 26, 26, .07);
  border: none;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #BA1A1A;
  font-size: 16px;
  flex-shrink: 0;
}
</style>
